// 視聴側 signaling WebSocket のライフサイクル。接続・メッセージ処理・
// 自動再接続 (指数バックオフ) ・ping・セッション終了警告を担当する。
// PeerConnection 操作は peer-connection、track DOM は media-elements、
// UI は status-ui / viewport に委譲する。

import {
  PINCODE_LENGTH,
  SESSION_WARNING_THRESHOLD_MS,
  SIGNALING_PING_INTERVAL_MS,
  SIGNALING_RECONNECT_MAX_ATTEMPTS,
  TERMINAL_CLOSE_REASONS,
} from "@wincast/shared";
import type { SignalingMessage, WindowMetadata } from "@wincast/shared";
import {
  abortableSleep,
  computeReconnectBackoff,
} from "@wincast/shared/lib/reconnect";
import { parseSignalingMessage } from "@wincast/shared/lib/parse-signaling";
import { getSignalingWsBase } from "./env.js";
import { t } from "./i18n.js";
import {
  clearVideos,
  detachAudioTrack,
  metadataByStreamId,
  videoByStreamId,
} from "./media-elements.js";
import {
  closePeerConnection,
  getPeerConnectionState,
  initPeerConnection,
  onRemoteIce,
  onRemoteOffer,
  setupPeerConnection,
} from "./peer-connection.js";
import {
  clearPincodeFromSession,
  getPincodeFromSession,
  savePincodeToSession,
} from "./session-store.js";
import {
  setFormStatus,
  setFormStatusKey,
  setStageStatusKey,
} from "./status-ui.js";
import { showJoin, showStage } from "./viewport.js";

type SignalingEls = {
  joinButton: HTMLButtonElement;
  sessionWarning: HTMLDivElement;
};

let els: SignalingEls;
// onWindowMetadata 後のレイアウト再計算 / teardown 時のレイアウト状態リセットは
// main.ts 側 (レイアウト層) に委譲する。
let refreshLayout: () => void;
let resetLayoutState: () => void;

let signalingSocket: WebSocket | null = null;
let pingIntervalId: number | null = null;
// シグナリング WS の自動再接続中であることを示すフラグ。null なら再接続なし
// (= 通常状態 or teardown 済み)。AbortController で reconnect ループを停止可能。
let reconnectAbort: AbortController | null = null;
let sessionEndsAt: number | null = null;
let sessionWarnIntervalId: number | null = null;

export function initSignalingClient(
  deps: SignalingEls,
  callbacks: { refreshLayout: () => void; resetLayoutState: () => void },
): void {
  els = deps;
  refreshLayout = callbacks.refreshLayout;
  resetLayoutState = callbacks.resetLayoutState;
  initPeerConnection({ sendSignaling });
}

export function teardown(): void {
  stopSignalingPing();
  stopSessionWarningTicker();
  sessionEndsAt = null;
  closePeerConnection();
  if (signalingSocket) {
    try {
      signalingSocket.close();
    } catch {
      // noop
    }
    signalingSocket = null;
  }
  clearVideos();
  detachAudioTrack();
  resetLayoutState();
}

// WebRTC 接続成立後はシグナリング WS が完全に idle になるため、Cloudflare の
// WS idle timeout (約 100 秒) で切断される。30 秒毎に ping を送って回避。
function startSignalingPing(): void {
  stopSignalingPing();
  pingIntervalId = window.setInterval(() => {
    sendSignaling({ type: "ping" });
  }, SIGNALING_PING_INTERVAL_MS);
}

function stopSignalingPing(): void {
  if (pingIntervalId !== null) {
    window.clearInterval(pingIntervalId);
    pingIntervalId = null;
  }
}

// セッション終了まで SESSION_WARNING_THRESHOLD_MS 以下になったら警告バナーを表示。
// 1 秒毎にカウントダウン。
function startSessionWarningTicker(): void {
  stopSessionWarningTicker();
  const tick = (): void => {
    if (sessionEndsAt === null) {
      els.sessionWarning.hidden = true;
      return;
    }
    const remaining = sessionEndsAt - Date.now();
    if (remaining <= 0) {
      els.sessionWarning.hidden = true;
      return;
    }
    if (remaining > SESSION_WARNING_THRESHOLD_MS) {
      els.sessionWarning.hidden = true;
      return;
    }
    const totalSec = Math.floor(remaining / 1000);
    const mm = Math.floor(totalSec / 60)
      .toString()
      .padStart(2, "0");
    const ss = (totalSec % 60).toString().padStart(2, "0");
    els.sessionWarning.textContent = t("session.endingIn", { mm, ss });
    els.sessionWarning.hidden = false;
  };
  tick();
  sessionWarnIntervalId = window.setInterval(tick, 1000);
}

function stopSessionWarningTicker(): void {
  if (sessionWarnIntervalId !== null) {
    window.clearInterval(sessionWarnIntervalId);
    sessionWarnIntervalId = null;
  }
  els.sessionWarning.hidden = true;
}

function sendSignaling(msg: SignalingMessage): void {
  if (!signalingSocket || signalingSocket.readyState !== WebSocket.OPEN) return;
  signalingSocket.send(JSON.stringify(msg));
}

// viewer 側 signaling WS の接続 URL を組み立てる。reconnect=true のときは
// サーバに「再接続 (= viewer-reconnected 通知)」を要求するクエリを付与する。
function buildViewerWsUrl(pincode: string, reconnect: boolean): string {
  const base = `${getSignalingWsBase()}/ws/viewer?pincode=${encodeURIComponent(pincode)}`;
  return reconnect ? `${base}&reconnect=1` : base;
}

export async function connect(pincode: string): Promise<void> {
  // 新規 connect では (たとえば前接続が reconnect 中だった場合に備え) 必ず
  // ループを止めてから始める。
  cancelReconnect();
  els.joinButton.disabled = true;
  setFormStatusKey("join.connecting", "info");

  const wsUrl = buildViewerWsUrl(pincode, false);
  const ws = new WebSocket(wsUrl);
  signalingSocket = ws;

  ws.addEventListener("open", () => {
    // 接続成立: 以降リロードしても自動で戻れるよう pincode を保存
    savePincodeToSession(pincode);
    setFormStatusKey("join.waitingHost", "info");
    setupPeerConnection();
    showStage();
    setStageStatusKey("join.waitingHost");
    startSignalingPing();
  });

  ws.addEventListener("message", (event) => {
    void handleSignaling(event.data as string);
  });

  ws.addEventListener("close", (event) => {
    handleClose(event.code, event.reason);
  });

  ws.addEventListener("error", () => {
    setFormStatusKey("join.connectFailed", "error", {}, "WebSocket error");
    els.joinButton.disabled = false;
    clearPincodeFromSession();
    teardown();
  });
}

async function handleSignaling(raw: string): Promise<void> {
  const msg = parseSignalingMessage(raw);
  if (!msg) return;

  switch (msg.type) {
    case "offer":
      await onRemoteOffer(msg.sdp);
      break;
    case "ice-candidate":
      await onRemoteIce(msg.candidate);
      break;
    case "window-metadata":
      onWindowMetadata(msg.windows);
      break;
    case "session-info":
      sessionEndsAt = msg.endsAt;
      startSessionWarningTicker();
      break;
    case "error":
      setFormStatusKey("join.serverError", "error", {}, msg.message);
      clearPincodeFromSession();
      teardown();
      showJoin();
      els.joinButton.disabled = false;
      break;
    default:
      break;
  }
}

function onWindowMetadata(windows: WindowMetadata[]): void {
  metadataByStreamId.clear();
  for (const w of windows) {
    metadataByStreamId.set(w.trackId, w);
    const video = videoByStreamId.get(w.trackId);
    if (video) {
      video.title = `${w.windowTitle} (${w.originalWidth}×${w.originalHeight})`;
      video.dataset.index = String(w.index);
    }
  }
  refreshLayout();
}

function handleClose(code: number, reason: string): void {
  // teardown() が先に走っているケース (leave ボタン等) では PC・WS とも既に
  // null。ここで再接続も後始末も走らせない。
  if (getPeerConnectionState() === null && signalingSocket === null) {
    cancelReconnect();
    return;
  }

  // 既存 WS の後片付けは共通: ping 停止 + ハンドル解放。
  // PC 自体は再接続トライ中も維持する (= teardown は呼ばない)。
  stopSignalingPing();
  signalingSocket = null;

  // サーバが明示した終了理由 (host-stopped / host-disconnected / session-expired /
  // replaced-by-new-*) は再接続不可なので pincode 破棄して終了する。
  if (TERMINAL_CLOSE_REASONS.includes(reason)) {
    cancelReconnect();
    clearPincodeFromSession();
    fullTeardownAfterClose(code, reason);
    return;
  }

  // P2P が一度確立されていれば一時切断とみなして再接続を試みる。close code では
  // なく reason で判定するのは、グレースフルなサーバ停止/デプロイも 1000 で閉じる
  // ため code だけでは意図的終了と区別できないから (上でターミナル理由は除外済み)。
  // "disconnected" は ICE 再探索中の瞬間状態で、WS だけ復旧すればセッション継続
  // できるので含める。pincode を sessionStorage に保持できていれば再 attach 可能。
  const pcState = getPeerConnectionState();
  const pcReusable = pcState === "connected" || pcState === "disconnected";
  const pincode = getPincodeFromSession();
  if (pcReusable && pincode && !reconnectAbort) {
    void startReconnect(pincode);
    return;
  }

  fullTeardownAfterClose(code, reason);
}

function fullTeardownAfterClose(code: number, reason: string): void {
  cancelReconnect();
  const wasConnected = getPeerConnectionState() === "connected";
  teardown();
  showJoin();
  els.joinButton.disabled = false;
  if (wasConnected) {
    // 接続中の切断は正常終了扱い (host stop / viewer leave 等)。
    // join 画面へ静かに戻すだけで、エラーメッセージは表示しない。
    setFormStatus("");
  } else {
    // そもそも接続できなかったケース = 失敗。pincode も破棄してリトライループ防止。
    clearPincodeFromSession();
    setFormStatusKey("join.connectFailed", "error", {}, reason || `code ${code}`);
  }
}

export function cancelReconnect(): void {
  if (reconnectAbort) {
    reconnectAbort.abort();
    reconnectAbort = null;
  }
}

async function startReconnect(pincode: string): Promise<void> {
  reconnectAbort = new AbortController();
  const signal = reconnectAbort.signal;
  setStageStatusKey("stage.tempDisconnect", "error");

  for (let attempt = 1; attempt <= SIGNALING_RECONNECT_MAX_ATTEMPTS; attempt++) {
    if (signal.aborted) return;
    try {
      await abortableSleep(computeReconnectBackoff(attempt), signal);
    } catch {
      return; // aborted
    }
    if (signal.aborted) return;

    const ok = await tryReconnectOnce(pincode, signal);
    if (ok) {
      reconnectAbort = null;
      return;
    }
  }

  // 試行回数を使い切った: 通常切断と同じ後始末
  reconnectAbort = null;
  fullTeardownAfterClose(1006, "reconnect-failed");
}

// 1 回分の WS 再接続を試みる。open まで届けば成功 = 既存ハンドラを再登録
// して signalingSocket に格納し、true を返す。失敗 (error / 早期 close) なら false。
function tryReconnectOnce(
  pincode: string,
  signal: AbortSignal,
): Promise<boolean> {
  return new Promise((resolve) => {
    const wsUrl = buildViewerWsUrl(pincode, true);
    const ws = new WebSocket(wsUrl);
    let settled = false;

    const settle = (ok: boolean): void => {
      if (settled) return;
      settled = true;
      signal.removeEventListener("abort", onAbort);
      resolve(ok);
    };

    const onAbort = (): void => {
      try {
        ws.close();
      } catch {
        // noop
      }
      settle(false);
    };
    signal.addEventListener("abort", onAbort);

    ws.addEventListener("open", () => {
      if (signal.aborted) {
        try {
          ws.close();
        } catch {
          // noop
        }
        settle(false);
        return;
      }
      // 接続確立: 永続ハンドラへ移行。PC は維持されたままなので、
      // ここでは setupPeerConnection を呼ばないし、Offer/Answer も期待しない。
      signalingSocket = ws;
      ws.addEventListener("message", (event) => {
        void handleSignaling(event.data as string);
      });
      ws.addEventListener("close", (event) => {
        handleClose(event.code, event.reason);
      });
      startSignalingPing();
      setStageStatusKey("stage.connected", "connected");
      settle(true);
    });

    ws.addEventListener("error", () => settle(false));
    ws.addEventListener("close", () => {
      // open 前に close したケース = 接続失敗扱い。
      if (!settled) settle(false);
    });
  });
}
